import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Select,
  DatePicker,
  TimePicker,
  message,
  InputNumber,
} from 'antd';
import { requiredRule } from 'utils/forms/validationRules';
import { REQUEST_LOADING } from 'constants/alerts';
import { ServicesService } from 'services/ServicesService';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';

function ApplyServiceForm({
  isActive,
  servicesList = [],
  servicesLoading,
  selectedClientId,
  onClose,
  onSuccess,
}) {
  const [form] = Form.useForm();

  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [selectedOptionId, setSelectedOptionId] = useState(null);

  const selectedService = useMemo(() => {
    return servicesList?.find((service) => service?.id === selectedServiceId);
  }, [selectedServiceId, servicesList]);

  const selectedOption = useMemo(() => {
    return selectedService?.options?.find(
      (option) => option.id === selectedOptionId
    );
  }, [selectedOptionId, selectedService?.options]);

  const handleSubmit = (values) => {
    const { date, time, promo, discount_price, discount_percent, ...rest } =
      values;

    const payload = {
      ...rest,
      start_date: `${date.format('YYYY-MM-DD')} ${time.format('HH:mm')}`,
      promo: promo || null,
      discount_percent: discount_percent || 0,
      discount_price: discount_price || 0,
      client_id: Number(selectedClientId),
    };

    handleCreate(payload);
  };

  const handleServiceChange = (value) => {
    setSelectedServiceId(value);

    if (selectedOptionId) {
      form.setFieldsValue({
        option_id: '',
      });
    }
  };

  const handleOptionChange = (value) => {
    setSelectedOptionId(value);
  };

  const handleCreate = async (data) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const success = await ServicesService.addServiceToClient(data);

      if (success) {
        onSuccess();
        form.resetFields();
        setSelectedServiceId(null);
        setSelectedOptionId(null);
        message.success('Услуга успешно присвоена');
      }
    } catch (err) {
      message.error('Не удалось присвоить услуги клиенту');
    } finally {
      hide();
    }
  };

  useEffect(() => {
    if (selectedOption) {
      form.setFieldsValue({
        payment_price: selectedOption.price,
      });
    }
  }, [selectedOption]);

  const handleRealPriceChange = (value) => {
    const updatedDiscount = (value / selectedOption.price) * 100;
    form.setFieldsValue({
      discount_percent: +updatedDiscount.toFixed(2),
      payment_price: +(
        selectedOption.price -
        (updatedDiscount / 100) * selectedOption.price
      ).toFixed(2),
    });
  };

  const handleDiscountChange = (value) => {
    const updatePrice = (value / 100) * selectedOption.price.toFixed(2);
    form.setFieldsValue({
      discount_price: +updatePrice.toFixed(2),
      payment_price: +(selectedOption.price - updatePrice).toFixed(2),
    });
  };

  return (
    <Drawer
      title="Добавление услуги клиенту"
      width="50%"
      onClose={onClose}
      visible={isActive}
      getContainer={false}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        labelAlign="right"
      >
        <Form.Item
          name="package_id"
          label="Услуга / Пакет"
          rules={[requiredRule]}
        >
          <Select
            allowClear
            showSearch
            filterOption={(inputValue, option) => {
              const key = option.key.toLowerCase();
              return key.includes(inputValue.toLowerCase());
            }}
            placeholder="Выбрать услугу / пакет"
            onChange={handleServiceChange}
            loading={servicesLoading}
          >
            {servicesList?.map(({ id, title }) => (
              <Select.Option key={title} value={id}>
                {title}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {selectedService && (
          <Form.Item name="option_id" label="Опция" rules={[requiredRule]}>
            <Select
              placeholder="Выбрать опцию"
              onChange={handleOptionChange}
              allowClear
            >
              {selectedService?.options?.map(({ id, title }) => (
                <Select.Option key={id} value={id}>
                  {title}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
        {selectedOption && selectedOption?.withPromo && (
          <Form.Item name="promo" label="Промо">
            <Input placeholder="Ввести промо" />
          </Form.Item>
        )}
        {selectedOption && (
          <Form.Item label="Изначальная цена">
            <Input disabled value={selectedOption?.price} readOnly />
          </Form.Item>
        )}
        <Form.Item name="discount_percent" label="Скидка %">
          <InputNumber
            suffix="%"
            onChange={handleDiscountChange}
            controls={false}
            placeholder="Введите скидку"
            max={100}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item name="discount_price" label="Скидка ₸">
          <InputNumber
            suffix="₸"
            onChange={handleRealPriceChange}
            controls={false}
            max={selectedOption?.price}
            placeholder="Введите скидку"
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item name="payment_price" label="Итоговая цена">
          <InputNumber
            placeholder="Итоговая цена"
            disabled
            suffix="₸"
            controls={false}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name="date" label="Дата" rules={[requiredRule]}>
          <DatePicker
            style={{ width: '100%' }}
            format="DD.MM.YYYY"
            placeholder="Выбрать дату начала"
          />
        </Form.Item>
        <Form.Item name="time" label="Время" rules={[requiredRule]}>
          <TimePicker format="HH:mm" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Добавить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { ApplyServiceForm };
